import dayjs from 'dayjs';
import {
  ForaEmployment,
  ForaPayrollResponse,
  ForaPerson,
} from '../types/Fora/Fora';
import {
  EmploymentPeriodView,
  WorkedMonth,
} from '../types/ApplicationV3/application';
import { PremiumPaymentThresholdContainer } from '../types/Config';

const ONGOING_END_DATE_SENTINEL = '9999-12-31';

export function getMonthsBetween(
  startDate: string,
  endDate: string
): { year: number; month: number }[] {
  const start = dayjs(startDate).startOf('month');
  const end = dayjs(endDate).startOf('month');
  const months: { year: number; month: number }[] = [];

  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    months.push({ year: current.year(), month: current.month() + 1 });
    current = current.add(1, 'month');
  }
  return months;
}

const hasPositivePayrollForMonth = (
  payrollMonths: ForaPayrollResponse[string] | undefined,
  periodKey: string
): boolean => {
  return (
    payrollMonths?.some(
      (payrollMonth) =>
        payrollMonth.period === periodKey &&
        payrollMonth.totalPremiumBasedPayAmount > 0
    ) ?? false
  );
};

const getWorkedMonthWindow = (
  payroll: ForaPayrollResponse,
  asOfDate: dayjs.Dayjs
) => {
  const currentMonthKey = asOfDate.format('YYYY-MM');
  const includeCurrentMonth = Object.values(payroll ?? {}).some(
    (payrollMonths) =>
      hasPositivePayrollForMonth(payrollMonths, currentMonthKey)
  );
  const referenceMonth = includeCurrentMonth
    ? asOfDate.startOf('month')
    : asOfDate.subtract(1, 'month').startOf('month');

  return {
    cutoffDate: referenceMonth.subtract(23, 'month').startOf('month'),
    maxEndDate: includeCurrentMonth ? asOfDate : referenceMonth.endOf('month'),
  };
};

export function transformForaResult(
  person: ForaPerson,
  payroll: ForaPayrollResponse,
  premiumPaymentThreshold: PremiumPaymentThresholdContainer,
  asOfDate: Date = new Date()
): EmploymentPeriodView[] {
  const today = dayjs(asOfDate);
  const { cutoffDate, maxEndDate } = getWorkedMonthWindow(payroll, today);

  return person.employments.flatMap((employment: ForaEmployment) => {
    const { employer, periods, employmentNumber } = employment;
    const { address } = employer;
    const formattedAddress = `${address.streetAddress}, ${address.postalCode} ${address.city}, ${address.countryCode}`;
    const employmentPayroll = payroll?.[employmentNumber] ?? [];

    return periods
      .map((period) => {
        const isOngoingEmployment =
          period.endDate?.startsWith(ONGOING_END_DATE_SENTINEL) ?? false;
        const periodStart = dayjs(period.startDate).startOf('month');
        const periodEnd = dayjs(period.endDate).endOf('month');

        // Skip periods that ended before the cutoff window
        if (periodEnd.isBefore(cutoffDate)) return null;

        // Limit range to the last 24 months
        const start = periodStart.isBefore(cutoffDate)
          ? cutoffDate
          : periodStart;
        const end = periodEnd.isAfter(maxEndDate) ? maxEndDate : periodEnd;

        if (end.isBefore(start)) return null;

        const months = getMonthsBetween(start.toISOString(), end.toISOString());

        const workedMonths: WorkedMonth[] = months.map((m) => {
          const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
          const hasPayroll = hasPositivePayrollForMonth(employmentPayroll, key);
          const payrollEntry = employmentPayroll.find((p) => p.period === key);
          const totalPremiumBasedPayAmount =
            payrollEntry?.totalPremiumBasedPayAmount ?? 0;
          const threshold = getPremiumPaymentThreshold(
            m,
            premiumPaymentThreshold
          );
          const meetsSalaryRequirement = employmentPayroll.some(
            (p) => p.period === key && p.totalPremiumBasedPayAmount >= threshold
          );
          return {
            ...m,
            hasPayroll,
            meetsSalaryRequirement,
            totalPremiumBasedPayAmount,
          };
        });
        return {
          id: crypto.randomUUID(),
          employmentNumber,
          organizationNumber: employer.organizationNumber,
          organizationName: employer.organizationName,
          address: formattedAddress,
          startDate: start.format('YYYY-MM-DD'),
          endDate: isOngoingEmployment
            ? today.format('YYYY-MM-DD')
            : end.format('YYYY-MM-DD'),
          workedMonths,
          retrievedFromFora: true,
          agreement: employment.productCoverages[0],
          temporaryEmploymentHasExpired: false,
        };
      })
      .filter(Boolean) as EmploymentPeriodView[];
  });
}

function getPremiumPaymentThreshold(
  m: { year: number },
  container: PremiumPaymentThresholdContainer
): number {
  const year = Number(m?.year);
  if (!Number.isFinite(year)) {
    return container.fallbackValue;
  }

  const match = container.valuesByYear?.[year];
  return match ?? container.fallbackValue;
}
